import { useState } from 'react';
import { DsButton } from '@/modules/internal-v2/ds';
import { useDeleteRole, useRoles } from '@/api/roles/roles.hooks';
import { Role } from '@/api/roles/roles.types';
import { ContainedPage } from '@/modules/internal-v2/shared/contained-page.component';
import { RoleEditor } from './components/role-editor.component';
import './roles.css';

interface EditorState {
  open: boolean;
  role: Role | null;
}

export function RolesPage() {
  const { data: roles, isLoading, isError } = useRoles();
  const deleteRole = useDeleteRole();
  const [editor, setEditor] = useState<EditorState>({
    open: false,
    role: null,
  });

  const handleDelete = (role: Role) => {
    if (window.confirm(`Rolle "${role.name}" wirklich löschen?`)) {
      deleteRole.mutate(role.id);
    }
  };

  return (
    <ContainedPage
      toolbar={
        <div className="roles-toolbar-content">
          <h1 className="roles-title">Rollen</h1>
          <span className="roles-count">
            {roles ? `${roles.length} Rollen` : ''}
          </span>
          <DsButton
            variant="primary"
            size="small"
            onClick={() => setEditor({ open: true, role: null })}
          >
            + Neue Rolle
          </DsButton>
        </div>
      }
      contained
    >
      {isLoading && <div className="roles-empty">Lädt…</div>}
        {isError && (
          <div className="roles-empty">Rollen konnten nicht geladen werden.</div>
        )}
        {roles && roles.length === 0 && (
          <div className="roles-empty">
            Noch keine Rollen. Lege die erste Rolle an.
          </div>
        )}
        {roles && roles.length > 0 && (
          <table className="ds-table roles-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Beschreibung</th>
                <th>Datensichtbarkeit</th>
                <th className="num">Berechtigungen</th>
                <th className="num">Benutzer</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr
                  key={role.id}
                  className="roles-row"
                  onClick={() => setEditor({ open: true, role })}
                >
                  <td>
                    <span className="roles-name">{role.name}</span>
                    {role.isSystem && (
                      <span className="ds-pill muted roles-system-pill">
                        System
                      </span>
                    )}
                  </td>
                  <td className="roles-desc">{role.description ?? '—'}</td>
                  <td>
                    {role.businessPartnerScope === 'ALL'
                      ? 'Alle Geschäftspartner'
                      : 'Eigene Geschäftspartner'}
                  </td>
                  <td className="num">{role.permissions.length}</td>
                  <td className="num">{role.assignedUserCount}</td>
                  <td className="roles-actions">
                    <DsButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditor({ open: true, role });
                      }}
                    >
                      {role.isSystem ? 'Ansehen' : 'Bearbeiten'}
                    </DsButton>
                    {!role.isSystem && (
                      <DsButton
                        size="small"
                        disabled={deleteRole.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(role);
                        }}
                      >
                        Löschen
                      </DsButton>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

      {editor.open && (
        <RoleEditor
          role={editor.role}
          onClose={() => setEditor({ open: false, role: null })}
        />
      )}
    </ContainedPage>
  );
}
